import React, {useEffect, useState} from 'react';

import {useAiUsage} from 'src/modules/ai';
import {EventCreateReviewView} from 'src/modules/ai/change-request-review/event-create-review/event-create-review.view';
import {
    EventReviewPushNotification,
} from 'src/modules/ai/change-request-review/event-create-review/event-review-push-notification';
import {getChangeRequestReview} from 'src/modules/ai/change-request-review/services';
import {checkRisksAndRecommendations} from 'src/modules/ai/change-request-review/utils/checkRisksAndRecommendations';
import {PushNotificationIds} from 'src/modules/event-create/constants';
import {
    getChangeRequestReviewById,
    getChangeRequestReviewByIdFailure, getChangeRequestReviewByIdSuccess,
} from 'src/openapi/apis/ChangeRequestReviewControllerApi';
import {useAppDispatch, useAppSelector} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

type Props = {
    changeRequestId: number | null,
};

const NOT_SAVED_CHANGE_REQUEST_MESSAGE = 'To run AI Review, please save the Change Request';
const ERROR_NOTIFICATION_MESSAGE = 'AI Review could not be completed. Please try again later.';
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventCreateReviewController = ({changeRequestId}: Props) => {
    const dispatch = useAppDispatch();
    const {onCreateNote} = usePushNotification();
    const {setAiUsed} = useAiUsage(changeRequestId || 0);
    const changeRequestReview = useAppSelector(getChangeRequestReview)?.[changeRequestId || 0];
    const [inReview, setInReview] = useState(false);

    useEffect(() => {
        if (inReview && changeRequestReview) {
            const hasRisks = checkRisksAndRecommendations(changeRequestReview);

            onCreateNote({
                id: getChangeRequestReviewByIdSuccess.type,
                status: hasRisks ? Status.WARNING : Status.SUCCESS,
                body: () => (
                    <EventReviewPushNotification hasRisksOrRecommendations={hasRisks}/>
                ),
                buttonProps: {icon: Icons.CLOSE},
                autoHideTimeout: 15000,
            });
            setInReview(false);
        }
    }, [inReview, changeRequestReview, onCreateNote]);

    const onAiReview = async () => {
        if (changeRequestId) {
            setInReview(true);
            const result = await dispatch(getChangeRequestReviewById({changeRequestId}));

            if (result.payload.success) {
                setAiUsed();
            }

            if (result.payload.error) {
                onCreateNote({
                    id: getChangeRequestReviewByIdFailure.type,
                    status: Status.ERROR,
                    message: ERROR_NOTIFICATION_MESSAGE,
                    buttonProps: {icon: Icons.CLOSE},
                    autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
                });
                setInReview(false);
            }

            return;
        }

        onCreateNote({
            id: PushNotificationIds.notSavedChangeRequest,
            status: Status.ERROR,
            message: NOT_SAVED_CHANGE_REQUEST_MESSAGE,
            buttonProps: {icon: Icons.CLOSE},
            autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
        });
    };

    return (
        <EventCreateReviewView
            inReview={inReview}
            isDisabled={Boolean(inReview || changeRequestReview)}
            onAiReview={onAiReview}
        />
    );
};
